import React, { useRef, useEffect, useState } from "react";
import {
  Stack,
  Divider,
  Box,
  Typography,
  Button,
  List,
  Fab,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  useTheme,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import PlaylistAddOutlined from "@mui/icons-material/PlaylistAddOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlined from "@mui/icons-material/ExpandLessOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";

import CopilotResponse from "./copilotResponse";
import Drawer from "@mui/material/Drawer";
import ConversationHistoryPanel from "./ConversationHistoryPanel";
import {
  createCopilotConversation,
  createCopilotPrompt,
  getCopilotConversation,
  getCopilotPrompt,
  getCopilotPrompts,
  updatePromptFromStream,
  setActiveConversation,
} from "slices/copilot";
import { updatePreferenceByUser } from "slices/auth";
import CopilotService from "services/copilot.service";

const PROMPT_MAX_LENGTH = 1000;
const PROMPT_WARNING_LENGTH = 900;
const SEARCH_AREAS = ["web", "opsblox", "company", "consumption"];

const ResponsePaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  flex: 1,
  minHeight: 0,
  margin: theme.spacing(1, 0, 0, 0),
  padding: theme.spacing(2),
  overflowY: "auto",
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.light,
  borderRadius: theme.spacing(0.7),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  position: "relative",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[600]
        : theme.palette.grey[300],
    borderRadius: "4px",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.grey[500]
          : theme.palette.grey[400],
    },
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  gap: theme.spacing(2),
  opacity: 0.6,
}));

/**
 * Reusable Copilot chat experience (messages, search-source options and prompt
 * input). Used both inside the right-hand drawer and on the full-page route so
 * the behaviour stays identical in both places.
 */
const CopilotChat = ({ headerActions = null, historyVariant = "persistent" }) => {
  const theme = useTheme();
  const listRef = useRef(null);
  const dispatch = useDispatch();

  const { user: currentUser } = useSelector((state) => state.auth);

  // Redux is the source of truth for which conversation is open. The
  // currentConversation preference is only a resume hint, applied once on mount.
  const currentConversationId = useSelector(
    (state) => state.copilot.activeConversationId
  );

  const preferredConversationId = useSelector(
    (state) =>
      state.auth?.user?.preferences?.find(
        (preference) => preference?.name === "currentConversation"
      )
  )?.preferenceValue;

  const currentConversation = useSelector((state) =>
    state.copilot.conversation.data.list.find(
      (conversation) => conversation.id === currentConversationId
    )
  );

  const prompts = useSelector((state) =>
    state.copilot.prompt.data.list
      .filter((prompt) => prompt.conversationId === currentConversationId)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
  );

  const promptsLoading = useSelector((state) => state.copilot.prompt.loading);

  // Only a fetch with nothing yet to show counts as "loading" for the pane.
  // Mid-conversation refetches (the streaming onDone re-read) also flip this
  // flag, and blanking a populated thread behind a spinner would be worse than
  // showing nothing at all.
  const isLoadingConversation = promptsLoading && prompts.length === 0;

  // Seed the active conversation from the saved preference exactly once.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!preferredConversationId) return;
    if (currentConversationId) {
      seededRef.current = true;
      return;
    }

    seededRef.current = true;
    dispatch(setActiveConversation(preferredConversationId));
  }, [preferredConversationId, currentConversationId, dispatch]);

  const lastPromptId =
    prompts.length > 0 ? prompts[prompts.length - 1].id : null;

  const [prompt, setPrompt] = useState("");
  const [promptLength, setPromptLength] = useState(0);
  // Persistent (full page) starts open; overlay (narrow drawer) starts closed.
  const [isHistoryOpen, setIsHistoryOpen] = useState(
    historyVariant === "persistent"
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Rendered immediately on send so the user's message and the "thinking"
  // bubble show up before the createCopilotPrompt round trip resolves —
  // without this the pane looks frozen for the length of that request.
  // Cleared once the real prompt lands in the Redux list (see the
  // isSubmitting-clearing effect below, which fires on the same trigger).
  const [optimisticPrompt, setOptimisticPrompt] = useState(null);

  const [searchAreas, setSearchAreas] = useState({
    everywhere: true,
    web: true,
    opsblox: true,
    company: true,
    consumption: true,
  });

  const isConversationIsWaitingForResponse = prompts?.some(
    (item) =>
      (!item.response || item.response.trim() === "") &&
      (item.jobStatus === "Pending" || item.jobStatus === "Running")
  );

  // Open an SSE stream for any prompt that is still pending/running.
  useEffect(() => {
    const pendingPrompts = prompts.filter(
      (p) =>
        (p.jobStatus === "Pending" || p.jobStatus === "Running") &&
        p.jobStatus !== "Error" &&
        p.jobStatus !== "Failed"
    );

    if (pendingPrompts.length === 0) return;

    const eventSources = pendingPrompts.map((p) =>
      CopilotService.streamCopilotPrompt(p.id, {
        onStatus: (statusText) => {
          dispatch(
            updatePromptFromStream({ id: p.id, streamStatus: statusText })
          );
        },
        onResponse: (responseText) => {
          dispatch(
            updatePromptFromStream({
              id: p.id,
              response: responseText,
              jobStatus: "Completed",
              streamStatus: null,
            })
          );
        },
        onError: () => {
          // Re-fetch from DB first — if the job actually completed (e.g. the
          // 90s inactivity timeout fired after a fast job), getCopilotPrompt
          // will return the real response and Completed status.
          // Do NOT dispatch updatePromptFromStream with jobStatus "Error" here
          // because it races with the DB fetch and can permanently mark a
          // successfully completed prompt as Error in the store.
          dispatch(getCopilotPrompt({ id: p.id }));
        },
        onDone: () => {
          // Mark the prompt Completed immediately so isWaiting flips to false
          // right away — don't wait for the DB re-fetch which may still show
          // jobStatus "Pending" if the jobs.go status write hasn't landed yet.
          dispatch(updatePromptFromStream({ id: p.id, jobStatus: "Completed", streamStatus: null }));
          // Re-fetch to pick up the full response text and any other fields
          // (handles the race where onResponse was missed before EventSource opened).
          dispatch(getCopilotPrompt({ id: p.id }));
          dispatch(getCopilotConversation({ id: currentConversationId }));
        },
      })
    );

    return () => {
      eventSources.forEach((es) => es.close());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts.map((p) => p.id).join(","), dispatch, currentConversationId]);

  useEffect(() => {
    if (!currentConversation && currentConversationId) {
      dispatch(getCopilotConversation({ id: currentConversationId }));
    }
  }, [currentConversation, dispatch, currentConversationId]);

  useEffect(() => {
    // Without a conversation there is nothing to fetch, and firing anyway would
    // flip prompt.loading on and show a spinner over the welcome state.
    if (!currentConversationId) return;

    const search = {
      id: currentConversationId,
      idField: "conversationId",
    };

    dispatch(getCopilotPrompts({ search }));
  }, [currentConversationId, dispatch]);

  useEffect(() => {
    // Only scroll to bottom if the user is already near the bottom or it's the
    // first exchange in the conversation.
    if (listRef.current) {
      const list = listRef.current;
      const isNearBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight < 100;

      if (isNearBottom || prompts.length <= 2) {
        setTimeout(() => {
          const lastItem = list.lastElementChild;
          if (lastItem) {
            lastItem.scrollIntoView({ behavior: "smooth", block: "end" });
          }
        }, 100);
      }
    }
  }, [prompts.length, optimisticPrompt]);

  // Clear isSubmitting/optimisticPrompt as soon as the newly created prompt
  // appears in the Redux list. At that point isConversationIsWaitingForResponse
  // takes over as the blocking signal, so neither is needed anymore.
  useEffect(() => {
    if (isSubmitting && prompts.length > 0) {
      setIsSubmitting(false);
      setOptimisticPrompt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts.length]);

  const { everywhere, web, opsblox, company, consumption } = searchAreas;

  const constructCommaSeparatedStringOfKnowledgeArea = () =>
    SEARCH_AREAS.filter((key) => searchAreas[key]).join(",");

  const handleSendPrompt = () => {
    if (!prompt.trim() || isSubmitting || isConversationIsWaitingForResponse) {
      return;
    }

    setIsSubmitting(true);
    // Show the message immediately, before the server has even acknowledged
    // the request — see the optimisticPrompt state declaration for why.
    setOptimisticPrompt(prompt);
    const copilotPrompt = {
      conversationId: currentConversationId,
      prompt,
      knowlageArea: constructCommaSeparatedStringOfKnowledgeArea(),
    };

    dispatch(createCopilotPrompt({ copilotPrompt })).then((result) => {
      // On failure the prompt never lands in Redux, so the prompts.length
      // effect below would never fire to clear these — clear them here
      // instead so the optimistic bubble and disabled input don't get stuck.
      if (result?.meta?.requestStatus === "rejected") {
        setIsSubmitting(false);
        setOptimisticPrompt(null);
      }
    });
    setPrompt("");
    setPromptLength(0);
    // On success, isSubmitting/optimisticPrompt are cleared by the useEffect
    // below once the new prompt appears in the Redux list.
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      handleSendPrompt();
    }
  };

  const handleUpdatePrompt = (event) => {
    setPrompt(event.target.value);
    setPromptLength(event.target.value.length);
  };

  const handleSelectConversation = (conversationId) => {
    if (conversationId === currentConversationId) return;

    // Update the UI immediately, then persist the resume hint. A failed
    // preference write only means the choice is not remembered across reloads.
    dispatch(setActiveConversation(conversationId));
    dispatch(
      updatePreferenceByUser({
        preference: {
          preference_value: conversationId,
          name: "currentConversation",
          username: currentUser.user.username,
        },
      })
    );
  };

  const handleNewChat = () => {
    // The current conversation is already empty — reuse it rather than
    // creating another orphan row that would clutter the history list.
    if (currentConversationId && prompts.length === 0) {
      return;
    }

    const copilotConversation = {
      user: currentUser.user.username,
    };
    dispatch(createCopilotConversation({ copilotConversation }));
  };

  const handleSearchAreaChange = (event) => {
    const { name, checked } = event.target;

    if (name === "everywhere") {
      setSearchAreas((prevState) => ({
        everywhere: checked,
        web: checked ? true : prevState.web,
        opsblox: checked ? true : prevState.opsblox,
        company: checked ? true : prevState.company,
        consumption: checked ? true : prevState.consumption,
      }));
    } else {
      setSearchAreas((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    }
  };

  const isInputDisabled = isSubmitting || isConversationIsWaitingForResponse;

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 0, width: "100%" }}>
      {historyVariant === "persistent" ? (
        <Box
          sx={{
            width: isHistoryOpen ? 260 : 0,
            flexShrink: 0,
            overflow: "hidden",
            borderRight: isHistoryOpen ? 1 : 0,
            borderColor: theme.palette.divider,
            transition: theme.transitions.create("width"),
          }}
        >
          {isHistoryOpen && (
            <ConversationHistoryPanel
              activeId={currentConversationId}
              onSelect={handleSelectConversation}
            />
          )}
        </Box>
      ) : (
        <Drawer
          open={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          variant="temporary"
          anchor="left"
          ModalProps={{ keepMounted: false }}
          PaperProps={{ sx: { width: 260, position: "absolute" } }}
          sx={{ position: "absolute", zIndex: theme.zIndex.drawer }}
        >
          <ConversationHistoryPanel
            activeId={currentConversationId}
            onSelect={(id) => {
              handleSelectConversation(id);
              setIsHistoryOpen(false);
            }}
          />
        </Drawer>
      )}

      <Box
        p={2}
        display="flex"
        flexDirection="column"
        height="100%"
        maxHeight="100%"
        sx={{
          backgroundColor: theme.palette.background.default,
          position: "relative",
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Stack gap={1} direction="row" alignItems="center">
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                borderRadius: "8px",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PsychologyOutlined sx={{ fontSize: "20px", color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}
              >
                Copilot
              </Typography>
              {isConversationIsWaitingForResponse && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      animation: "pulse 1.5s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.3 },
                      },
                    }}
                  />
                  Processing...
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <Tooltip
              title={isHistoryOpen ? "Hide chat history" : "Show chat history"}
            >
              <IconButton
                size="small"
                aria-label="Toggle chat history"
                onClick={() => setIsHistoryOpen((open) => !open)}
                sx={{ color: theme.palette.primary.main }}
              >
                <HistoryOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            {headerActions}
            <Button
              variant="contained"
              color="primary"
              onClick={handleNewChat}
              size="small"
              endIcon={<PlaylistAddOutlined />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              New Chat
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Conversation Title */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentConversation?.name ||
              (isLoadingConversation ? "Loading…" : "New Conversation")}
          </Typography>
          {prompts.length > 0 && (
            <Chip
              label={`${prompts.length} ${
                prompts.length === 1 ? "message" : "messages"
              }`}
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: "20px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.alt
                    : theme.palette.background.light,
              }}
            />
          )}
        </Box>

        {/* Chat Messages */}
        <ResponsePaper variant="outlined">
          {isLoadingConversation ? (
            <EmptyStateBox>
              <CircularProgress size={32} />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Loading conversation…
              </Typography>
            </EmptyStateBox>
          ) : prompts.length === 0 && !optimisticPrompt ? (
            <EmptyStateBox>
              <PsychologyOutlined
                sx={{ fontSize: "64px", color: theme.palette.text.disabled }}
              />
              <Typography
                variant="h6"
                sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              >
                How can I help you today?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.disabled,
                  textAlign: "center",
                  maxWidth: "80%",
                }}
              >
                Ask me anything about your cloud infrastructure, costs, or
                optimizations
              </Typography>
            </EmptyStateBox>
          ) : (
            <List
              ref={listRef}
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              {prompts.map((item) => (
                <React.Fragment key={item.id}>
                  <CopilotResponse
                    key={item.id + "_prompt"}
                    user={currentUser}
                    message={item.prompt}
                    id={item.id + "_prompt"}
                    timestamp={item.createdAt}
                  />
                  <CopilotResponse
                    key={item.id + "_response"}
                    user="Copilot"
                    message={item.response}
                    isWaiting={
                      (!item.response || item.response.trim() === "") &&
                      (item.jobStatus === "Pending" || item.jobStatus === "Running")
                    }
                    streamStatus={item.streamStatus}
                    id={item.id + "_response"}
                    lastPrompt={lastPromptId === item.id}
                    copilotPrompt={item}
                    timestamp={item.modifiedAt || item.updatedAt || item.createdAt}
                  />
                </React.Fragment>
              ))}
              {/* Optimistic pair: shown from the moment Send is clicked until
                  createCopilotPrompt's response adds the real prompt to Redux.
                  Without this the pane shows nothing during that request. */}
              {optimisticPrompt && (
                <React.Fragment key="optimistic">
                  <CopilotResponse
                    key="optimistic_prompt"
                    user={currentUser}
                    message={optimisticPrompt}
                    id="optimistic_prompt"
                    timestamp={new Date().toISOString()}
                  />
                  <CopilotResponse
                    key="optimistic_response"
                    user="Copilot"
                    message=""
                    isWaiting
                    id="optimistic_response"
                    lastPrompt={false}
                    copilotPrompt={{}}
                  />
                </React.Fragment>
              )}
            </List>
          )}
        </ResponsePaper>

        {/* Search Options */}
        <Box display="flex" flexDirection="column" mt={2} mb={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            sx={{ cursor: "pointer" }}
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                Search Sources
              </Typography>
              <Tooltip title="Choose where Copilot searches for information">
                <InfoOutlined
                  sx={{ fontSize: "14px", color: theme.palette.text.disabled }}
                />
              </Tooltip>
            </Box>
            <IconButton size="small">
              {showAdvancedOptions ? (
                <ExpandLessOutlined />
              ) : (
                <ExpandMoreOutlined />
              )}
            </IconButton>
          </Box>

          <Collapse in={showAdvancedOptions}>
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.alt
                    : theme.palette.background.light,
                padding: theme.spacing(1.5),
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <FormGroup component="fieldset" sx={{ gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={everywhere}
                      onChange={handleSearchAreaChange}
                      name="everywhere"
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      Everywhere
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 0.5,
                    ml: 3,
                    opacity: everywhere ? 0.5 : 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={web}
                        onChange={handleSearchAreaChange}
                        name="web"
                        size="small"
                        disabled={everywhere}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Web
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={opsblox}
                        onChange={handleSearchAreaChange}
                        name="opsblox"
                        size="small"
                        disabled={everywhere}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.primary,
                        }}
                      >
                        OpsBlox
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={company}
                        onChange={handleSearchAreaChange}
                        name="company"
                        size="small"
                        disabled={everywhere}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Company
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consumption}
                        onChange={handleSearchAreaChange}
                        name="consumption"
                        size="small"
                        disabled={everywhere}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Consumption
                      </Typography>
                    }
                  />
                </Box>
              </FormGroup>
            </Box>
          </Collapse>
        </Box>

        {/* Input Area */}
        <Box sx={{ position: "relative" }}>
          <TextField
            id="prompt"
            placeholder="Ask me anything... (Shift + Enter for new line)"
            multiline
            rows={4}
            inputProps={{ maxLength: PROMPT_MAX_LENGTH }}
            helperText={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      promptLength > PROMPT_WARNING_LENGTH
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  }}
                >
                  {promptLength} / {PROMPT_MAX_LENGTH}
                </Typography>
                {promptLength > PROMPT_WARNING_LENGTH && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.error.main }}
                  >
                    Character limit approaching
                  </Typography>
                )}
              </Box>
            }
            value={prompt}
            onChange={handleUpdatePrompt}
            onKeyDown={handleKeyDown}
            disabled={isInputDisabled}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.alt
                    : theme.palette.background.light,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : theme.palette.grey[50],
                },
                "&.Mui-focused": {
                  backgroundColor: theme.palette.background.paper,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
            }}
          />
          <Tooltip
            title={
              isSubmitting
                ? "Sending…"
                : isConversationIsWaitingForResponse
                ? "Please wait for the current response"
                : !prompt.trim()
                ? "Type a message first"
                : "Send message (Enter)"
            }
          >
            <span>
              <Fab
                onClick={handleSendPrompt}
                disabled={!prompt.trim() || isInputDisabled}
                size="small"
                aria-label={isSubmitting ? "Sending message" : "Send message"}
                sx={{
                  position: "absolute",
                  bottom: 28,
                  right: 16,
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "scale(1.05)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                  boxShadow:
                    prompt.trim() && !isInputDisabled
                      ? `0 4px 12px ${theme.palette.primary.main}40`
                      : "none",
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  <SendOutlined />
                )}
              </Fab>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default CopilotChat;
